import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import "dayjs/locale/en-gb"; // Import the locale for British English
import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectUserData,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import { useDispatch } from "react-redux";
import {
  setCostWeightchartData,
  setPerformanceByKeywords,
  setRelevantAccountsBasedOnSite,
  setSelectedLPAccount,
  setSelectedLPCampaign,
  setSelectedLPDateRange,
  setSelectedLPSharedUid,
  setSiteDimensions,
} from "../../../../features/landingPageExperienceSlice";
import { isGreaterEndDate } from "../../../../utils/helpers";
import { CustomDateRangePicker } from "../../../ReusableDateRangePickerUI/CustomDateRangePicker";
import { useLPExperience } from "../../../../pages/PaidTrafficManagement/hooks/useLPExperience";

const LandingPageExperienceGetData = () => {
  const dispatch = useDispatch();
  // 👇 Add this ONCE at the top of your component
  const isFirstLoadRef = useRef(true);
  const isFirstLoadDimensionsRef = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    updateURLParamsOfLP,
    fetchedResults,
    clearParams,
    loadingCampaigns,
    isActiveCampaigns,
    setIsActiveCampaigns,
  } = useLPExperience();

  const response = useSelector(selectUserData);
  const {
    selectedLPSharedUid,
    selectedLPCampaign,
    allAdvertisingAccounts,
    relevantAccountsBasedOnSite,
    selectedLPAccount,
    selectedLPDateRange,
    allCampaigns,
  } = useSelector((state: any) => state.landingPageExperience);

  //-- Get relevant accounts based on the selected Website
  const getRelevantAccountsBasedOnSite = (siteSharedUid: string) => {
    // 0) Allowed sources (according to requirements)
    const ALLOWED_SOURCES = new Set(["g_ads_search", "bing_ads_search"]);

    // 1) First: accounts that have the selected LP shared_uid in any linked asset
    const byLanding = allAdvertisingAccounts.filter((account: any) =>
      account?.linked_assets?.some(
        (asset: any) => asset?.websites__shared_uid === siteSharedUid
      )
    );

    // 2) Then: keep only active accounts
    const activeOnly = byLanding.filter(
      (account: any) => account?.active === true
    );

    // 3) Then: keep only accounts from allowed traffic sources
    const finalResults = activeOnly.filter((account: any) =>
      ALLOWED_SOURCES.has(account?.traffic_source)
    );

    dispatch(setRelevantAccountsBasedOnSite(finalResults));

    // if (finalResults.length > 0) {
    //   dispatch(setSelectedLPAccount(finalResults[0].account_id)); // Auto-select if only one
    // } else {
    //   dispatch(setSelectedLPAccount(null)); // Reset otherwise
    // }
  };

  //-- when selection changes in the Autocomplete for websites
  const handleWebsitesChange = (_: unknown, newValue: any) => {
    if (newValue?.shared_uid !== selectedLPSharedUid) {
      clearParams();
      if (newValue) {
        dispatch(setSelectedLPSharedUid(newValue.shared_uid));
        getRelevantAccountsBasedOnSite(newValue.shared_uid);
        dispatch(setSiteDimensions(newValue?.dimensions));
      } else {
        dispatch(setSelectedLPSharedUid(""));
      }
    }
  };

  //-- Click on Get Data button
  const handleGetLPData = async () => {
    dispatch(setCostWeightchartData([]));
    dispatch(setPerformanceByKeywords([]));
    updateURLParamsOfLP(true); //set url query parameters
    fetchedResults(); // get data from API
  };

  //Get data from URL while we reload
  useEffect(() => {
    if (!isFirstLoadRef.current) return; // Skip after first load

    const siteFromURL = searchParams.get("selectedSite");
    const accountFromURL = searchParams.get("account");
    const dateRangeFromURL = searchParams.get("lp_date_range");
    const campaignFromURL = searchParams.get("campaign");
    const activeCampaignFromURL = searchParams.get("is_active_campaigns");

    if (
      siteFromURL &&
      siteOptions.some((s: any) => s.shared_uid === siteFromURL)
    ) {
      dispatch(setSelectedLPSharedUid(siteFromURL));
    }

    if (accountFromURL) {
      dispatch(setSelectedLPAccount(accountFromURL));
    }

    if (dateRangeFromURL) {
      const [startStr, endStr] = dateRangeFromURL.split("&");
      if (dayjs(startStr).isValid() && dayjs(endStr).isValid()) {
        dispatch(setSelectedLPDateRange([dayjs(startStr), dayjs(endStr)]));
      }
    }

    if (campaignFromURL) {
      dispatch(setSelectedLPCampaign(campaignFromURL));
    }
    if (activeCampaignFromURL) {
      const isActiveCampaigns = activeCampaignFromURL === "true";
      if (isActiveCampaigns) {
        setIsActiveCampaigns(true);
      } else {
        setIsActiveCampaigns(false);
      }
    }

    isFirstLoadRef.current = false; // All restore done
    // eslint-disable-next-line
  }, []);

  // restore campaign AFTER campaigns are fetched when reloading
  useEffect(() => {
    const campaignFromURL = searchParams.get("campaign");
    if (!campaignFromURL) return;

    if (allCampaigns.length > 0) {
      const foundCampaign = allCampaigns.find(
        (camp: any) => String(camp.camp_id) === campaignFromURL
      );
      if (foundCampaign) {
        dispatch(setSelectedLPCampaign(foundCampaign.camp_id));
      }
    }
    //eslint-disable-next-line
  }, [searchParams, allCampaigns]);

  // Get Relevant account based on selected website(from allAdvertisingAccounts) while reloading
  useEffect(() => {
    if (!selectedLPSharedUid || allAdvertisingAccounts?.length === 0) return;

    // 0) Allowed sources (according to requirements)
    const ALLOWED_SOURCES = new Set(["g_ads_search", "bing_ads_search"]);

    // 1) accounts that have the selected LP shared_uid in any linked asset
    const byLanding = allAdvertisingAccounts.filter((account: any) =>
      account?.linked_assets?.some(
        (asset: any) => asset?.websites__shared_uid === selectedLPSharedUid
      )
    );

    // 2) keep only active accounts
    const activeOnly = byLanding.filter(
      (account: any) => account?.active === true
    );

    // 3) keep only accounts from allowed traffic sources
    const finalResults = activeOnly.filter((account: any) =>
      ALLOWED_SOURCES.has(account?.traffic_source)
    );

    dispatch(setRelevantAccountsBasedOnSite(finalResults));
    //eslint-disable-next-line
  }, [selectedLPSharedUid, allAdvertisingAccounts]);

  //-- Get website list those have permisssions
  const siteOptions = useMemo(() => {
    return (
      response?.data?.website_permissions?.map((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0]; // shared_uid of the site
        const site_name = siteObj[shared_uid].ui_site_name; // Site name
        const dimensions = siteObj[shared_uid].dimensions; // Dimensions of the sites
        return { shared_uid, site_name, dimensions }; // Return as an object with shared_uid and site_name
      }) || []
    );
  }, [response]);

  // While reloading, fetch dimensions once the site data is available
  useEffect(() => {
    if (!isFirstLoadDimensionsRef.current) return;
    if (!selectedLPSharedUid || siteOptions.length === 0) return;

    const matchedSite = siteOptions.find(
      (site: any) => site.shared_uid === selectedLPSharedUid
    );

    dispatch(setSiteDimensions(matchedSite ? matchedSite.dimensions : []));
    isFirstLoadDimensionsRef.current = false;
    //eslint-disable-next-line
  }, [selectedLPSharedUid, siteOptions]);

  //when reload is is_clicked true and all url params are there then api calls will be triggered
  useEffect(() => {
    const selectedSite = searchParams.get("selectedSite");
    const account = searchParams.get("account");
    const lpDateRange = searchParams.get("lp_date_range");
    const siteDimensions = searchParams.get("site_dimensions") ?? "[]";
    const isClicked = searchParams.get("is_clicked");

    const parsedDimensions = JSON.parse(siteDimensions);
    if (!Array.isArray(parsedDimensions) || parsedDimensions.length === 0)
      return;

    // Fire only if ALL query params exist and valid
    if (!selectedSite || !account || !lpDateRange || !siteDimensions) return;
    if (isClicked !== "true") return;
    try {
      // All conditions met → fetch
      fetchedResults();
    } catch (e) {
      console.error("Invalid site_dimensions in URL:", e);
    }
    // eslint-disable-next-line
  }, []);

  // by default date range set for last 30 days
  useEffect(() => {
    const dateRangeFromURL = searchParams.get("lp_date_range");
    if (dateRangeFromURL) {
      const [startStr, endStr] = dateRangeFromURL.split("&");
      if (dayjs(startStr).isValid() && dayjs(endStr).isValid()) {
        dispatch(setSelectedLPDateRange([dayjs(startStr), dayjs(endStr)]));
      }
    } else {
      dispatch(setSelectedLPDateRange([dayjs().subtract(30, "day"), dayjs()]));
    }
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <Paper sx={{ padding: "8px 16px", mb: 2 }}>
        <Typography fontSize={24}>Landing Page Experience</Typography>
        <Typography sx={{ color: "#00000099", paddingBottom: "16px" }}>
          Improve Quality Score and campaign efficiency by finding and
          optimizing high-cost keywords with poor landing page experience.
        </Typography>
        <Grid container mt={0} columnSpacing={2} mb={1}>
          <Grid size={5}>
            <Autocomplete
              data-tracker="437"
              disablePortal
              sx={{ width: "100%" }}
              options={siteOptions}
              getOptionLabel={(option: any) => option?.site_name || ""}
              value={
                siteOptions?.find(
                  (option: any) => option?.shared_uid === selectedLPSharedUid
                ) || null
              }
              onChange={handleWebsitesChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Select Asset"
                  size="small"
                  color="secondary"
                  placeholder="Search site name or choose from the list"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={2}>
            <Autocomplete
              data-tracker="438"
              disabled={!selectedLPSharedUid}
              sx={{ width: "100%" }}
              options={relevantAccountsBasedOnSite}
              getOptionLabel={(option: any) => option?.account_name || ""}
              value={
                relevantAccountsBasedOnSite?.find(
                  (option: any) => option?.account_id === selectedLPAccount
                ) || null
              }
              onChange={(_, newValue) => {
                if (newValue) {
                  setIsActiveCampaigns(true);
                  dispatch(setSelectedLPAccount(newValue.account_id));
                  dispatch(setSelectedLPCampaign(""));
                } else {
                  dispatch(setSelectedLPAccount(""));
                  setIsActiveCampaigns(false);
                  dispatch(setSelectedLPCampaign("")); // reset campaign as depend on account
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  placeholder="Select Account"
                  label="Account"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  }}
                  color="secondary"
                />
              )}
              size="small"
            />
          </Grid>
          <Grid size={2}>
            <CustomDateRangePicker
              isRequired={true}
              dataTracker="439"
              value={selectedLPDateRange}
              onChange={(newValue: any) => {
                const [startDate, endDate] = newValue || [];

                if (
                  startDate &&
                  endDate &&
                  isGreaterEndDate({ startDate, endDate })
                ) {
                  dispatch(
                    setDisplayErrorSnackBar({
                      message: "Start date cannot be after end date",
                      type: "error",
                    })
                  );
                  return;
                }

                dispatch(setSelectedLPDateRange([startDate, endDate]));
              }}
            />
          </Grid>
          <Grid size={2}>
            <Autocomplete
              data-tracker="440"
              fullWidth
              disablePortal
              disabled={
                !selectedLPSharedUid || !selectedLPAccount || loadingCampaigns
              }
              sx={{ width: "100%" }}
              options={allCampaigns}
              getOptionLabel={(option: any) => option?.camp_name || ""}
              value={
                allCampaigns?.find(
                  (option: any) => option?.camp_id === selectedLPCampaign
                ) || null
              }
              onChange={(_, newValue) => {
                if (newValue) {
                  dispatch(setSelectedLPCampaign(newValue.camp_id));
                } else {
                  dispatch(setSelectedLPCampaign(""));
                  const updatedParams = new URLSearchParams(searchParams);
                  updatedParams.delete("campaign");
                  setSearchParams(updatedParams, { replace: true });
                }
              }}
              //  loading={loadingCampaigns}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    loadingCampaigns ? "Loading..." : "Select From List"
                  }
                  label="Campaign (optional)"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loadingCampaigns ? (
                          <CircularProgress color="secondary" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  color="secondary"
                />
              )}
              size="small"
            />
            {selectedLPSharedUid && selectedLPAccount && (
              <FormControlLabel
                control={
                  <Checkbox
                    color="secondary"
                    checked={isActiveCampaigns}
                    onChange={(e) => setIsActiveCampaigns(e.target.checked)}
                  />
                }
                label="Only Active Campaigns"
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          <Grid size={1}>
            <Button
              data-tracker="441"
              variant="contained"
              disabled={
                !selectedLPSharedUid ||
                !selectedLPAccount ||
                !selectedLPDateRange?.[0] || // startDate missing
                !selectedLPDateRange?.[1] // endDate missing
              }
              onClick={handleGetLPData}
              fullWidth
              size="large"
              color="secondary"
              sx={{ py: "7px", px: "10px", whiteSpace: "nowrap" }}
            >
              Get Data
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default LandingPageExperienceGetData;
